import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { User, Lock, Trash2, Save } from 'lucide-react';

export default function AccountPage() {
  const { user, refreshUser, logout } = useAuth();
  const [tab, setTab] = useState('Profile');
  const [profile, setProfile] = useState({
    full_name: user?.full_name || '',
    company_name: user?.company_name || '',
  });
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [loading, setLoading] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put('/users/profile', profile);
      await refreshUser();
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) return toast.error('Passwords do not match');
    if (passwords.new.length < 8) return toast.error('Password must be at least 8 characters');
    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwords.current,
        new_password: passwords.new
      });
      toast.success('Password changed successfully');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Password change failed');
    } finally {
      setLoading(false);
    }
  };

  const deleteAccount = async () => {
    const confirm1 = window.confirm('Are you sure you want to delete your account? All data will be deleted in 7 days.');
    if (!confirm1) return;
    const confirm2 = window.prompt('Type DELETE to confirm:');
    if (confirm2 !== 'DELETE') return;

    try {
      await api.post('/users/delete-account');
      toast.success('Account deletion scheduled. Check your email.');
      logout();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="page">
      <h1 className="page-title">Account Settings</h1>

      <div className="tabs">
        {['Profile', 'Security'].map(t => (
          <button key={t} className={`tab ${tab === t ? 'tab--active' : ''}`} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {tab === 'Profile' && (
        <div className="tab-content">
          <form onSubmit={saveProfile} className="form-container">
            <div className="form-section">
              <h3>Personal Information</h3>

              <div className="user-profile-header">
                <div className="profile-avatar">{user?.full_name?.charAt(0)?.toUpperCase()}</div>
                <div>
                  <strong>{user?.full_name}</strong>
                  <span>{user?.email}</span>
                  <span className="badge">{user?.plan?.toUpperCase()}</span>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input className="input" value={profile.full_name}
                    onChange={e => setProfile(p => ({ ...p, full_name: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>Company Name</label>
                  <input className="input" value={profile.company_name}
                    onChange={e => setProfile(p => ({ ...p, company_name: e.target.value }))} />
                </div>
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input className="input" value={user?.email} disabled />
                <small>Email cannot be changed. Contact support if needed.</small>
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading}>
                <Save size={16} /> {loading ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </form>
        </div>
      )}

      {tab === 'Security' && (
        <div className="tab-content">
          <form onSubmit={changePassword} className="form-container">
            <div className="form-section">
              <h3>Change Password</h3>

              <div className="form-group">
                <label>Current Password</label>
                <input className="input" type="password" value={passwords.current}
                  onChange={e => setPasswords(p => ({ ...p, current: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label>New Password</label>
                <input className="input" type="password" value={passwords.new}
                  onChange={e => setPasswords(p => ({ ...p, new: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <input className="input" type="password" value={passwords.confirm}
                  onChange={e => setPasswords(p => ({ ...p, confirm: e.target.value }))} required />
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading}>
                <Lock size={16} /> {loading ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </form>

          <div className="form-section danger-zone">
            <h3>Danger Zone</h3>
            <div className="danger-item">
              <div>
                <strong>Delete Account</strong>
                <p>Permanently delete your account and all agents. This takes effect in 7 days.</p>
              </div>
              <button className="btn btn-danger" onClick={deleteAccount}>
                <Trash2 size={16} /> Delete Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
